import { add, endOfDay, format, isSameDay, startOfDay } from 'date-fns';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createSelector } from 'reselect';
import { Shift, useConfig } from '@nexelem/config';
import { useEffect } from 'react';
import { getCurrentShift } from '../../ShiftSelector';
import { useKanbanEventStore } from '../Kanban';

export type DateRange = { from: Date; to: Date };

export const KanbanFilter = {
  TO_ACCEPT: 'toAccept',
  SHOW_WITHOUT_TASKS: 'showWithoutTasks',
  PRIORITY: 'priority',
  DELAYED: 'delayed',
} as const;

export type KanbanFilter = (typeof KanbanFilter)[keyof typeof KanbanFilter];

type KanbanFilterStore = {
  shifts: Shift[];
  selectedShifts: Shift['nameKey'][];
  kanbanDate: Date;
  kanbanDateRange: DateRange;
  kanbanFilters: KanbanFilter[];
  setShifts: (shifts: Shift[]) => void;
  setSelectedShifts: (selectedShifts: Shift['nameKey'][]) => void;
  setKanbanDate: (date: Date) => void;
  setKanbanFilters: (kanbanFilters: KanbanFilter[]) => void;
  reset: () => void;
};

export const useKanbanFilterStore = create(
  immer<KanbanFilterStore>((set, get, store) => {
    return {
      shifts: [],
      selectedShifts: [],
      kanbanDate: new Date(),
      kanbanDateRange: {
        from: new Date(),
        to: new Date(),
      },
      kanbanFilters: [],
      setShifts: (shifts: Shift[]) => {
        const currentShift = getInitialKanbanShiftSelectorState(shifts);
        return currentShift ? set(currentShift) : set({ shifts });
      },
      setSelectedShifts: (selectedShifts: Shift['nameKey'][]) => {
        const kanbanDateRange = getKanbanDateRange(get().shifts, selectedShifts, get().kanbanDate);
        useKanbanEventStore.getState().reset();
        return set({ selectedShifts, kanbanDateRange });
      },
      setKanbanDate: (date: Date) => {
        const kanbanDateRange = getKanbanDateRange(get().shifts, get().selectedShifts, date);
        useKanbanEventStore.getState().reset();
        return set({ kanbanDate: date, kanbanDateRange: kanbanDateRange });
      },
      setKanbanFilters: (kanbanFilters) => {
        useKanbanEventStore.getState().reset();
        return set({ kanbanFilters });
      },
      reset: () => {
        const { shifts: _emptyShifts, ...rest } = store.getInitialState();
        const { shifts } = get();
        const currentShift = getInitialKanbanShiftSelectorState(shifts);
        return currentShift ? set({ ...rest, ...currentShift }) : set(rest);
      },
    };
  }),
);

const getInitialKanbanShiftSelectorState = (shifts: Shift[]) => {
  const currentShift = getCurrentShift(shifts);

  if (currentShift) {
    return {
      shifts,
      selectedShifts: [currentShift.nameKey],
      kanbanDateRange: {
        from: currentShift.from,
        to: currentShift.to,
      },
    };
  }
};

export const useLoadShiftsEffect = () => {
  const { modules } = useConfig();
  const setShifts = useKanbanFilterStore((state) => state.setShifts);
  useEffect(() => {
    setShifts(modules.production.workerModeViewShifts);
  }, [modules.production.workerModeViewShifts, setShifts]);
};

export const isInitialKanbanState = createSelector(
  (state: KanbanFilterStore) => state.kanbanDateRange,
  (state: KanbanFilterStore) => state.kanbanFilters,
  (state: KanbanFilterStore) => state.shifts,
  (state: KanbanFilterStore) => state.selectedShifts,
  (kanbanDateRange, kanbanFilters, shifts, selectedShifts) => {
    const initialState = useKanbanFilterStore.getInitialState().kanbanDateRange;
    const currentShift = getCurrentShift(shifts);
    return (
      isSameDay(kanbanDateRange.from, initialState.from) &&
      isSameDay(kanbanDateRange.to, initialState.to) &&
      kanbanFilters.length === 0 &&
      selectedShifts.length === 1 &&
      selectedShifts[0] === currentShift?.nameKey
    );
  },
);

const getKanbanDateRange = (shifts: Shift[], selectedShifts: Shift['nameKey'][], date: Date): DateRange => {
  const sortedShifts = selectedShifts.toSorted();
  const firstShiftKey = sortedShifts[0];
  const lastShiftKey = sortedShifts[sortedShifts.length - 1];
  const firstShift = shifts.find((shift) => shift.nameKey === firstShiftKey);
  const lastShift = shifts.find((shift) => shift.nameKey === lastShiftKey);

  if (firstShift && lastShift) {
    return {
      from: new Date(`${format(date, 'yyyy-MM-dd')} ${firstShift.from}`),
      to: add(new Date(`${format(date, 'yyyy-MM-dd')} ${lastShift.to}`), {
        days: lastShift.endNextDay ? 1 : 0,
        seconds: -1,
      }),
    };
  }

  return {
    from: startOfDay(date),
    to: endOfDay(date),
  };
};
